import { ChangeEvent, FC, useCallback } from 'react';

import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import { StepAdvancedConfigurationWithErrorHandlingPanel } from 'dashboard/components/WorkflowControlPanel/StepAdvancedConfigurationWithErrorHandlingPanel';
import ConditionalConfiguration from 'dashboard/components/WorkflowControlPanel/StepConfigConditional/ConditionalConfiguration';
import EditConditionalPanel from 'dashboard/components/WorkflowControlPanel/StepConfigConditional/EditConditionalPanel';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { UsableObjectConfig } from 'dashboard/lib/workflows';
import { Trigger } from 'dashboard/models/trigger';
import { WorkflowError, WorkflowSchema } from 'dashboard/models/workflow';
import {
  WorkflowConditional,
  WorkflowStep,
  WorkflowStepLoop,
  WorkflowStepLoopWhileSettings,
} from 'dashboard/models/workflow-version';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import useToggle from 'lib/hooks/useToggle';
import { JsonSchema } from 'lib/json-schema';
import { TokensToNode } from 'lib/workflows';

interface WhileSettingsProps {
  editable: boolean;
  errors: WorkflowError[];
  jsonSchema: JsonSchema;
  settings: WorkflowStepLoopWhileSettings;
  step: WorkflowStepLoop;
  tokensToDisplayValue: Record<string, string>;
  tokensToSteps: TokensToNode;
  trigger?: Trigger;
  usableObjects: UsableObjectConfig[];
  workflowSchema: WorkflowSchema;
  onChangeMaxIterations: (event: ChangeEvent<HTMLInputElement>) => void;
  updateStep: (newStep: WorkflowStep) => void;
}

const WhileSettings: FC<WhileSettingsProps> = ({
  editable,
  errors,
  jsonSchema,
  settings,
  step,
  tokensToDisplayValue,
  tokensToSteps,
  trigger,
  workflowSchema,
  usableObjects,
  onChangeMaxIterations,
  updateStep,
}) => {
  const { hasErrorFor, renderErrorMessageFor } = useWorkflowError<'max-iterations'>(errors);
  const [conditionalPanelOpened, { enable: openConditionalPanel, disable: closeConditionalPanel }] =
    useToggle(false);

  const updateConditional = useCallback(
    (newConditional: WorkflowConditional) => {
      updateStep({
        ...step,
        settings: {
          ...settings,
          conditional: newConditional,
        },
      });
    },
    [settings, step, updateStep],
  );
  return (
    <>
      <ParamLabel htmlFor='step-config-loop__maximum-iterations' labelText='Maximum iterations' />
      <Input
        aria-invalid={hasErrorFor('max-iterations')}
        disabled={!editable}
        hasError={hasErrorFor('max-iterations')}
        id='step-config-loop__maximum-iterations'
        type='number'
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        value={settings['max-iterations']}
        onChange={onChangeMaxIterations}
      />
      {renderErrorMessageFor('max-iterations')}
      <Spacer height={2} />
      <ConditionalConfiguration
        editable={editable}
        errors={errors}
        conditional={settings.conditional}
        jsonSchema={jsonSchema}
        tokensToDisplayValue={tokensToDisplayValue}
        tokensToSteps={tokensToSteps}
        usableObjects={usableObjects}
        onEditClick={openConditionalPanel}
        headingText='Condition'
      />
      <Spacer height={2} />
      <StepAdvancedConfigurationWithErrorHandlingPanel
        step={step}
        trigger={trigger}
        updateStep={updateStep}
        disabled={!editable}
      />
      {editable && conditionalPanelOpened && (
        <EditConditionalPanel
          conditional={settings.conditional}
          panelTitle='Edit condition'
          workflowSchema={workflowSchema}
          jsonSchema={jsonSchema}
          tokensToDisplayValue={tokensToDisplayValue}
          allowEditName
          updateConditional={updateConditional}
          onClose={closeConditionalPanel}
        />
      )}
    </>
  );
};

export default WhileSettings;
